import { useEffect, useState, useSyncExternalStore } from "react";
import { PALETTE, type ChartTheme } from "@cd/viz";

export type ThemePref = "system" | "light" | "dark";

const media = () => window.matchMedia("(prefers-color-scheme: dark)");

function readPref(): ThemePref {
  try {
    return (localStorage.getItem("theme") as ThemePref) || "system";
  } catch {
    return "system";
  }
}

function apply(pref: ThemePref) {
  const dark = pref === "dark" || (pref === "system" && media().matches);
  document.documentElement.dataset.theme = dark ? "dark" : "light";
}

export function useThemePref() {
  const [pref, setPref] = useState<ThemePref>(readPref);
  useEffect(() => {
    apply(pref);
    try {
      localStorage.setItem("theme", pref);
    } catch {
      /* private mode */
    }
    if (pref !== "system") return;
    const m = media();
    const on = () => apply("system");
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, [pref]);
  return [pref, setPref] as const;
}

/** Resolved theme, reactive to changes of <html data-theme>. */
export function useResolvedTheme(): "light" | "dark" {
  return useSyncExternalStore(
    (cb) => {
      const obs = new MutationObserver(cb);
      obs.observe(document.documentElement, { attributes: true, attributeFilter: ["data-theme"] });
      return () => obs.disconnect();
    },
    () => (document.documentElement.dataset.theme === "dark" ? "dark" : "light"),
  );
}

export function chartTheme(mode: "light" | "dark"): ChartTheme {
  return mode === "dark"
    ? {
        palette: [...PALETTE.dark], comparison: "#898781", text: "#ffffff", muted: "#898781", grid: "#2c2c2a",
        background: "#1a1a19", tooltipBg: "#222220", tooltipBorder: "rgba(255,255,255,0.12)",
      }
    : {
        palette: [...PALETTE.light], comparison: "#a9a79f", text: "#0b0b0b", muted: "#898781", grid: "#e1e0d9",
        background: "#fcfcfb", tooltipBg: "#ffffff", tooltipBorder: "rgba(11,11,11,0.10)",
      };
}
